import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    Text,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Badge,
    IconButton,
    Tooltip
} from '@chakra-ui/react';
import { ViewIcon } from '@chakra-ui/icons';
import { toast } from 'react-toastify';
import offerLetterService from 'services/Recruit/services/offerLetter';
import DOMPurify from 'dompurify';

const TemplateSelector = ({ onSelect, selectedTemplateId, isOfferUpdation = false, isDisabled = false }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        }
    }, [isOpen]);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await offerLetterService.getTemplates({ allData: true, isActive: true });
            setTemplates(response.data || []);
        } catch (error) {
            toast.error('Failed to load templates');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async (templateId) => {
        setPreviewLoading(true);
        try {
            onClose();
            const response = await offerLetterService.previewTemplate(templateId);
            setPreviewData(response);
            onPreviewOpen();
        } catch (error) {
            toast.error('Failed to preview template');
            console.error(error);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handlePreviewClose = () => {
        onPreviewClose();
        onOpen();
    };

    const handleSelect = (template) => {
        onSelect(template);
        onClose();
    };

    const selectedTemplate = templates.find(t => t._id === selectedTemplateId);

    return (
        <>
            <Box>
                <Flex justify="space-between" align="center" mb={2}>
                    {!isOfferUpdation && <Text fontWeight="medium">Template</Text>}
                    {isOfferUpdation ? <Button
                        fontSize="14px"
                        onClick={onOpen}
                        pl={'12px!important'}
                        pr={'12px!important'}
                        pt={'4px!important'}
                        pb={'4px!important'}
                        borderRadius={'8px!important'}
                        fontWeight="500"
                        className="backBtn"
                        disabled={isDisabled}

                        _disabled={{
                            bg: "transparent!important",
                            color: "#285AE7",
                            cursor: "not-allowed",
                            opacity: 0.5,
                        }}
                    >
                        {selectedTemplate ? "Change Template" : "Select Template"}
                    </Button>

                        : <Button size="sm" onClick={onOpen} colorScheme="blue" variant="outline">
                            {selectedTemplate ? 'Change Template' : 'Select Template'}
                        </Button>}

                </Flex>
                {selectedTemplate && !isOfferUpdation && (
                    <Box p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                        <Flex justify="space-between" align="center">
                            <Box>
                                <Text fontWeight="semibold">{selectedTemplate.name}</Text>
                                <Text fontSize="sm" color="gray.600">{selectedTemplate.description}</Text>
                            </Box>
                            <Badge colorScheme="blue">{selectedTemplate.category}</Badge>
                        </Flex>
                    </Box>
                )}
            </Box>

            {/* Template Selection Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent maxW="800px" maxH="90vh">
                    <ModalHeader>Select Offer Letter Template</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} overflowY="auto">
                        {loading ? (
                            <Text>Loading templates...</Text>
                        ) : templates.length === 0 ? (
                            <Text>No active templates found</Text>
                        ) : (
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                {templates.map((template) => (
                                    <Box
                                        key={template._id}
                                        p={4}
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        cursor="pointer"
                                        _hover={{ borderColor: 'blue.400', shadow: 'md' }}
                                        onClick={() => handleSelect(template)}
                                        bg={selectedTemplateId === template._id ? 'blue.50' : 'white'}
                                    >
                                        <Flex justify="space-between" align="start" mb={2}>
                                            <Box flex="1">
                                                <Text fontWeight="semibold" mb={1}>{template.name}</Text>
                                                <Badge colorScheme="purple" mb={2}>{template.category}</Badge>
                                            </Box>
                                            <Tooltip label="Preview">
                                                <IconButton
                                                    size="sm"
                                                    icon={<ViewIcon />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePreview(template._id);
                                                    }}
                                                    variant="ghost"
                                                />
                                            </Tooltip>
                                        </Flex>
                                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                            {template.description}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500" mt={2}>
                                            Used {template.usageCount} times
                                        </Text>
                                    </Box>
                                ))}
                            </Grid>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Template Preview Modal */}
            <Modal isOpen={isPreviewOpen} onClose={handlePreviewClose} size="xl">
                <ModalOverlay />
                <ModalContent maxW="900px" maxH="90vh">
                    <ModalHeader>Template Preview</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} overflowY="auto">
                        {previewLoading ? (
                            <Text>Loading preview...</Text>
                        ) : previewData ? (
                            <Box>
                                <Box mb={4} p={3} bg="gray.50" borderRadius="md">
                                    <Text mb={0} fontWeight="semibold">{previewData.template?.name}</Text>
                                    <Text fontSize="sm" color="gray.600">{previewData.template?.description}</Text>
                                </Box>
                                <Box mb={4}>
                                    <Text fontWeight="semibold" mb={2}>Subject:</Text>
                                    <Text>{previewData.preview?.subject}</Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold" mb={2}>Content:</Text>
                                    <Box
                                        p={4}
                                        bg="white"
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                    >
                                        <style>
                                            {`
                                            ul {
                                                padding-left: 4px !important;
                                                margin: 8px 0 !important;
                                                list-style-position: inside;
                                            }
                                            li {
                                                margin-bottom: 4px;
                                            }
                                            `}
                                        </style>
                                        <Box
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.preview?.content) }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default TemplateSelector;